import React, { useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ResultRating } from "@/components/ResultRating";
import { formatError } from "@/lib/api";
import { toast } from "sonner";

const TONE = {
  default: "border-border hover:border-primary/60 hover:bg-primary/5",
  ok: "border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10",
  warn: "border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10",
  danger: "border-rose-500/40 bg-rose-500/5 hover:bg-rose-500/10",
};
const ICON = { default: "text-primary", ok: "text-emerald-600 dark:text-emerald-400", warn: "text-amber-600 dark:text-amber-400", danger: "text-rose-600 dark:text-rose-400" };

/** شريط إدارة القناة: أزرار كبيرة واضحة (إضافة / إيقاف الرد / إزالة) بنفس أسلوب خطوات التوليد. */
export const ChannelActions = ({ title, hint, actions }) => (
  <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="channel-actions">
    <div>
      <div className="font-extrabold text-sm">{title}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
      {actions.filter(Boolean).map((a) => (
        <button key={a.id} type="button" onClick={a.onClick} disabled={a.disabled} className={`rounded-xl border p-3 text-start transition-colors disabled:opacity-50 ${TONE[a.tone || "default"]}`} data-testid={`channel-action-${a.id}`}>
          <a.icon className={`w-5 h-5 mb-1.5 ${ICON[a.tone || "default"]}`} />
          <div className="text-sm font-bold leading-tight">{a.label}</div>
          {a.desc && <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{a.desc}</div>}
        </button>
      ))}
    </div>
  </div>
);

/**
 * حوار إجراء موحّد: شرح → (حقول اختيارية) → تنفيذ → نجاح + تقييم التجربة (نفس آلية تقييم نتائج التوليد).
 * fields: [{ key, label, placeholder, type: 'text'|'textarea', required, dir }]
 */
export const ActionDialog = ({ open, onClose, title, body, cta, tone = "default", fields = [], run, onDone, ratingProduct, ratingId, projectId, successTitle, successBody }) => {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const close = () => { setVals({}); setDone(null); onClose?.(done ? "done" : "cancel"); };
  const go = async () => {
    for (const f of fields) if (f.required && !(vals[f.key] || "").trim()) return toast.error(`${f.label} مطلوب`);
    setBusy(true);
    try { const r = await run(vals); setDone(r || {}); onDone?.(r); } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); } finally { setBusy(false); }
  };
  const danger = tone === "danger";
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md" dir="rtl" data-testid="action-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2">{danger && !done && <AlertTriangle className="w-5 h-5 text-rose-500" />}{done ? (successTitle || "تم بنجاح") : title}</DialogTitle></DialogHeader>
        {!done ? (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground leading-relaxed" data-testid="action-dialog-body">{body}</div>
            {fields.map((f) => (
              <div key={f.key}>
                <label className="text-xs font-bold">{f.label}{f.required && <span className="text-rose-500"> *</span>}</label>
                {f.type === "textarea"
                  ? <Textarea rows={2} value={vals[f.key] || ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} placeholder={f.placeholder} dir={f.dir} className="mt-1 text-sm" data-testid={`action-field-${f.key}`} />
                  : <Input value={vals[f.key] || ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} placeholder={f.placeholder} dir={f.dir} className="mt-1" data-testid={`action-field-${f.key}`} />}
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <Button onClick={go} disabled={busy} className={`flex-1 font-bold ${danger ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`} data-testid="action-dialog-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : cta}</Button>
              <Button variant="outline" onClick={close} disabled={busy} data-testid="action-dialog-cancel">إلغاء</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="action-dialog-success">
            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <div>{typeof successBody === "function" ? successBody(done) : successBody}</div>
            </div>
            {ratingProduct && <div><div className="text-xs font-bold mb-1">كيف كانت تجربتك في هذه الخطوة؟</div><ResultRating product={ratingProduct} contentId={ratingId || done.id || ratingProduct} projectId={projectId} className="mt-0" /></div>}
            <Button onClick={close} className="w-full font-bold" data-testid="action-dialog-done">إغلاق</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
